import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import Layout from '../components/layout';
import Seo from '@shared/min';
import { ContentBody } from '@lanl-web/lanl-components';
import { replaceElements } from '../utils';

import {
  ContentContainer,
  CurrentConditionsBanner,
  LinksTable,
  RawDataTable,
  AllSections,
} from '../components';

import './page.scss';

const PageTemplate = ({ data, location, pageContext }) => {
  const {
    directus: { wm_page },
  } = data;

  const page = wm_page[0];
  const title = page?.title ?? '';
  const body = page?.body ?? '';
  const summaries = page?.chart_summaries ?? [];
  const rawData = pageContext?.rawData ?? false;
  const sections = page?.sections ?? [];

  const renderRawDataTable = () => {
    return rawData && <RawDataTable />;
  };

  return (
    <Layout className='page-template' location={location}>
      <CurrentConditionsBanner />
      <ContentContainer location={location} showBreadCrumb>
        <h1 className='page-title'>{title}</h1>
        <p className='date'>
          {moment.tz('America/Denver').format('MMMM DD, YYYY')}
        </p>

        {body && <ContentBody body={replaceElements(body)} />}

        {renderRawDataTable()}

        {summaries.map(({ description, title, chart_resources }, index) => (
          <LinksTable
            key={`links-table-${index}`}
            description={description ?? ''}
            title={title ?? ''}
            resources={chart_resources}
          />
        ))}

        <AllSections data={sections} />
      </ContentContainer>
    </Layout>
  );
};

PageTemplate.defaultProps = {
  data: {},
};

PageTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object,
};

export const PageTemplateQuery = graphql`
  query ($slug: String!) {
    directus {
      wm_page(filter: { slug: { _eq: $slug } }) {
        title
        slug
        body
        sections {
          collection
          item {
            ... on DirectusData_wm_charts {
              id
              name
              data
              tower
              status
              source_url
              sort
              chart_type
              labels
            }
            ... on DirectusData_wm_tables {
              id
              name
              status
              sort
            }
            ... on DirectusData_wm_data_request {
              id
              show
            }
            ... on DirectusData_wm_body {
              id
              title
              content
            }
            ... on DirectusData_wm_chart_summary {
              id
              title
              description
              chart_resources {
                wm_resources_id {
                  group_name
                  id
                  image_file {
                    filename_disk
                    filename_download
                  }
                  image_link
                  is_document
                  link
                  name
                  document {
                    filename_download
                    filename_disk
                  }
                }
              }
            }
            ... on DirectusData_wm_video {
              id
              name
              section_title
              video_id
            }
          }
        }
        chart_summaries {
          title
          description
          chart_resources {
            wm_resources_id {
              group_name
              id
              image_file {
                filename_disk
                filename_download
              }
              image_link
              is_document
              link
              name
              document {
                filename_download
                filename_disk
              }
            }
          }
        }
        page_description
        keywords
      }
    }
  }
`;

export default PageTemplate;

export const Head = ({ data }) => {
  const {
    directus: { wm_page: page },
  } = data;

  const { title, page_description, keywords } = page[0];

  return (
    <Seo
      title={`${title} | Weather`}
      description={
        page_description ??
        'The Weather Machine provides access to the latest meteorological observations, climatological information, and weather forecast products for the Los Alamos area.'
      }
      keywords={keywords ?? ''}
    />
  );
};
